import React from 'react'
import { MainTemplate } from '../../templates/MainTemplate'
import { TableTemplate } from '../../templates/TableTemplate'
import { CreatePropertyModal } from '../../organisms/modal/CreatePropertyModal'
import { useProperty } from './hook'
import { DetailPropertyModal } from '../../organisms/modal/DetailPropertyModal';
import { PropertyFormData } from '../../organisms/form/PropertyForm';
import { PropertySearchForm } from '../../organisms/form/PropertySearchForm';
import { EditPropertyModal } from '../../organisms/modal/EditPropertyModal'
import { exportPropertiesToCSV } from '../../../utils/csv'
import { useDispatch } from 'react-redux'
import { AppDispatch } from '../../../store'
import { appActions } from '../../../ducks/app'
import { PropertyExportApi } from '../../../core/http/httpClient'
import { Resource, Permission } from '../../../constants/permissions'
import { usePropertyToast } from './hook';
import { useResponsive } from '../../../hooks/useResponsive';
import styled from 'styled-components';
import { Toast } from '../../atoms/Toast';

// Helper to map Properties to PropertyFormData for edit forms (needs IDs)
function mapPropertiesToFormData(property: any): PropertyFormData {
  const mappedData = {
    propertyCode: property.property_code || '',
    propertyName: property.property_name || '',
    propertyCategory: property.property_category?.id?.toString() || '',
    propertyType: property.property_type?.id?.toString() || '',
    propertyStatus: property.property_status || '',
    owner: property.owner?.id?.toString() || '',
    city: property.city?.id?.toString() || '',
    district: property.district?.id?.toString() || '',
    commune: property.commune?.id?.toString() || '',
    price: property.price?.toString() || '',
    size: property.size?.toString() || '',
    landArea: property.land_area?.toString() || '',
    bedrooms: property.bedrooms !== undefined && property.bedrooms !== null ? property.bedrooms.toString() : '',
    bathrooms: property.bathrooms !== undefined && property.bathrooms !== null ? property.bathrooms.toString() : '',
    popularPlace: property.popular_place?.id?.toString() || '',
    features: property.features && property.features.length > 0 ? property.features.map((f: any) => f.id?.toString() || f) : [],
    linkYoutube: property.youtube_link || '',
    googleMapLink: property.google_map_link || '',
    description: property.description || '',
    images: property.property_images?.map((img: any) => img.image) || [],
    coverImage: property.thumbnail_image || null,
    isAvailable: typeof property.is_available === 'boolean' ? property.is_available : Boolean(property.is_available),
    isHot: typeof property.is_hot === 'boolean' ? property.is_hot : Boolean(property.is_hot),
  };

  return mappedData;
}

// Helper to map Properties to PropertyFormData for detail view (display names)
function mapPropertiesToDetailData(property: any): PropertyFormData {
  const mappedData = {
    propertyCode: property.property_code || '',
    propertyName: property.property_name || '',
    propertyCategory: property.property_category?.name || property.property_category?.id?.toString() || '',
    propertyType: property.property_type?.name || property.property_type?.id?.toString() || '',
    propertyStatus: property.property_status || '',
    owner: property.owner?.name || property.owner?.id?.toString() || '',
    city: property.city?.name || property.city?.id?.toString() || '',
    district: property.district?.name || property.district?.id?.toString() || '',
    commune: property.commune?.name || property.commune?.id?.toString() || '',
    price: property.price?.toString() || '',
    size: property.size?.toString() || '',
    landArea: property.land_area?.toString() || '',
    bedrooms: property.bedrooms !== undefined && property.bedrooms !== null ? property.bedrooms.toString() : '',
    bathrooms: property.bathrooms !== undefined && property.bathrooms !== null ? property.bathrooms.toString() : '',
    popularPlace: property.popular_place?.name || property.popular_place?.id?.toString() || '',
    features: property.features && property.features.length > 0 ? property.features.map((f: any) => f.name || f.id?.toString() || f) : [],
    linkYoutube: property.youtube_link || '',
    googleMapLink: property.google_map_link || '',
    description: property.description || '',
    images: property.property_images?.map((img: any) => img.image) || [],
    coverImage: property.thumbnail_image || null,
    isAvailable: typeof property.is_available === 'boolean' ? property.is_available : Boolean(property.is_available),
    isHot: typeof property.is_hot === 'boolean' ? property.is_hot : Boolean(property.is_hot),
  };

  return mappedData;
}

export const PropertyPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { isMobile } = useResponsive();

  const {
    tableHeaders,
    properties,
    totalItems,
    currentPage,
    pageSize,
    fetchDataFunction,
    isDetailModalOpen,
    selectedProperty,
    detailLoading,
    handleCloseDetailModal,
    refreshPropertyDetails,
    hasPagination,
    handlePageChange,
    handleRowDelete,
    setSelectedPropertyEdit,
    selectedPropertyEdit,
    handleCloseCreateModal,
    handleCloseEditModal,
    handleOpenCreateModal,
    handleOpenEditModal,
    isCreateModalOpen,
    isEditModalOpen,
    // Filters
    searchText,
    setSearchText,
    selectedStatus,
    setSelectedStatus,
    selectedType,
    setSelectedType,
    propertyTypes,
    loading,
    refetchProperties,
    selectedHot,
    setSelectedHot,
    toast,
    showToast,
  } = useProperty();

  const handleExportClick = () => {
    dispatch(
      appActions.showModal({
        title: "Export",
        subTitle: `Are you sure you want to export properties to CSV?`,
        buttons: [
          {
            text: "Cancel",
            variant: "border",
            onClick: () => dispatch(appActions.hideModal()),
          },
          {
            text: "Export",
            variant: "filled",
            onClick: async () => {
              dispatch(appActions.hideModal());
              dispatch(appActions.showLoading());

              try {
                // Build export parameters to include current filters
                const params: any = {};
                if (searchText) params.search = searchText;
                if (selectedStatus) params.status = selectedStatus;
                if (selectedType) params.type = selectedType;
                if (selectedHot) params.is_hot = selectedHot;

                // Fetch all properties without pagination
                const response = await PropertyExportApi.call(params as any);

                if (response.data.data && response.data.data.length > 0) {
                  exportPropertiesToCSV(response.data.data, 'properties.csv');
                  dispatch(appActions.hideLoading());
                } else {
                  dispatch(appActions.hideLoading());
                  dispatch(
                    appActions.showModal({
                      title: "No Data",
                      subTitle: "No properties found to export.",
                      buttons: [
                        {
                          text: "OK",
                          variant: "filled",
                          onClick: () => dispatch(appActions.hideModal()),
                        }
                      ]
                    })
                  );
                }
              } catch (error) {
                dispatch(appActions.hideLoading());
                dispatch(
                  appActions.showModal({
                    title: "Export Failed",
                    subTitle: "Failed to export properties. Please try again.",
                    buttons: [
                      {
                        text: "OK",
                        variant: "filled",
                        onClick: () => dispatch(appActions.hideModal()),
                      }
                    ]
                  })
                );
              }
            }
          }
        ]
      })
    );
  };




  return (
    <StyledPropertyPageContainer $isMobile={isMobile}>
      <MainTemplate
        title="Properties"
        useFitHeight
        onPermissionError={showToast}
        button={{
          text: "Export",
          variant: "filled",
          onClick: handleExportClick,
          permission: {
            resource: Resource.PROPERTIES,
            permission: Permission.EXPORT,
          },
        }}
      >
        <PropertySearchForm
          searchValue={searchText}
          onSearchChange={setSearchText}
          statusValue={selectedStatus}
          onStatusChange={setSelectedStatus}
          typeValue={selectedType}
          onTypeChange={setSelectedType}
          typeOptions={propertyTypes}
          onCreateClick={handleOpenCreateModal}
          searchPlaceholder="Search by property name and code..."
          isCreateButtonDisabled={isCreateModalOpen}
          hotValue={selectedHot}
          onHotChange={setSelectedHot}
        />
        <TableTemplate
          headers={tableHeaders}
          data={properties}
          loading={loading}
          fetchDataFunction={fetchDataFunction}
          total={totalItems}
          perPage={pageSize}
          currentPage={currentPage}
          hasPagination={hasPagination}
          onSetPage={handlePageChange}
          columnWidths={isMobile ? ["10%", "25%", "10%", "10%", "20%", "20%", "2%", "3%"] : ["8%", "25%", "12%", "10%", "20%", "20%", "2%", "3%"]} // Adjusted for Hot and Actions columns
          editable
          onEdit={(property) => {
            setSelectedPropertyEdit(property);
            handleOpenEditModal()
          }}
          showHeaderDeleteIcon={false}
          deletable
          resource={Resource.PROPERTIES}
          onPermissionError={showToast}
          onDelete={handleRowDelete}
          stickyHeader
        />
      </MainTemplate>
      <CreatePropertyModal
        isOpen={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        refetchProperties={refetchProperties}
      />
      {selectedProperty && isDetailModalOpen && (
        <DetailPropertyModal
          isOpen={isDetailModalOpen}
          onClose={handleCloseDetailModal}
          property={detailLoading ? undefined : mapPropertiesToDetailData(selectedProperty)}
        />
      )}

      {selectedPropertyEdit && (
        <EditPropertyModal
          isOpen={isEditModalOpen}
          onClose={handleCloseEditModal}
          defaultValues={mapPropertiesToFormData(selectedPropertyEdit)}
          propertyId={selectedPropertyEdit.id}
          refetchProperties={refetchProperties}
          onSuccess={() => {
            // Refresh the property details if the detail modal is open for this property
            if (selectedProperty && selectedProperty.id === selectedPropertyEdit.id) {
              refreshPropertyDetails(selectedProperty.id);
            }
          }}
        />
      )}
      {toast.visible && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => { }} // visibility handled by visible flag in hook
        />
      )}
    </StyledPropertyPageContainer>
  );
}

const StyledPropertyPageContainer = styled.div<{ $isMobile?: boolean }>`
  width: 100%;
  height: 100%;
  
  ${({ $isMobile }) =>
    $isMobile
      ? `
      -webkit-overflow-scrolling: touch;
      `
      : ""}
`;
